import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import * as React from "react"

import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import { useSession } from "@/lib/session"

import {
  useDeleteIntervention,
  useIntervention,
  useUpdateIntervention,
  type InterventionIn,
} from "@/features/interventions/api"
import { ConfirmDelete } from "@/features/interventions/confirm-delete"
import { InterventionEditorModal } from "@/features/interventions/editor-modal"
import { InterventionView } from "@/features/interventions/intervention-view"
import { DocumentsPanel } from "@/features/documents/panel"
import {
  NO_ERRORS,
  submitErrorsFrom,
  type SubmitErrors,
} from "@/features/interventions/validation"

export const Route = createFileRoute("/interventions/$interventionId")({
  component: InterventionDetailScreen,
})

/**
 * Read on the page; edit in an overlay card.
 *
 * Creation lives on the list screen as the same overlay, per the design
 * reference: an editor opens over the screen it belongs to rather than
 * swapping the page for a form, so the person keeps their place.
 */
function InterventionDetailScreen() {
  const { interventionId } = Route.useParams()

  // The old create-as-a-page deep link: the list now opens the editor card.
  if (interventionId === "new") return <RedirectToCatalogue />

  const id = Number(interventionId)
  if (!Number.isInteger(id)) {
    return (
      <>
        <BackLink />
        <PageHeader title="Intervention" />
        <Card>
          <CardContent>
            <ErrorState
              title="That is not an intervention"
              description="That web address does not name an intervention."
            />
          </CardContent>
        </Card>
      </>
    )
  }

  return <EditIntervention id={id} />
}

function RedirectToCatalogue() {
  const navigate = useNavigate()
  React.useEffect(() => {
    void navigate({ to: "/interventions", replace: true })
  }, [navigate])
  return null
}

function EditIntervention({ id }: { id: number }) {
  const navigate = useNavigate()
  const { canWrite, canDelete } = useSession()
  const query = useIntervention(id)
  const update = useUpdateIntervention()
  const remove = useDeleteIntervention()

  const [editing, setEditing] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const [errors, setErrors] = React.useState<SubmitErrors>(NO_ERRORS)
  const [saved, setSaved] = React.useState(false)

  if (query.isPending) {
    return (
      <>
        <BackLink />
        <PageHeader title="Intervention" />
        <DetailSkeleton />
      </>
    )
  }

  if (query.isError) {
    return (
      <>
        <BackLink />
        <PageHeader title="Intervention" />
        <Card>
          <CardContent>
            <ErrorState
              description="This intervention could not be loaded."
              onRetry={() => void query.refetch()}
            />
          </CardContent>
        </Card>
      </>
    )
  }

  const intervention = query.data
  if (!intervention) {
    return (
      <>
        <BackLink />
        <PageHeader title="Intervention" />
        <Card>
          <CardContent>
            <EmptyState
              title="No such intervention"
              description="It may have been deleted since this link was made."
              action={
                <Button variant="secondary" onClick={() => void navigate({ to: "/interventions" })}>
                  Back to the catalogue
                </Button>
              }
            />
          </CardContent>
        </Card>
      </>
    )
  }

  function submit(payload: InterventionIn) {
    setErrors(NO_ERRORS)
    setSaved(false)
    update.mutate(
      { id, body: payload },
      {
        onSuccess: () => {
          setEditing(false)
          setSaved(true)
        },
        onError: (cause) => setErrors(submitErrorsFrom(cause)),
      },
    )
  }

  return (
    <>
      <BackLink />
      <PageHeader
        eyebrow="Libraries"
        title={intervention.name}
        description="A course in the catalogue, delivered at training events."
        actions={
          <>
            {canWrite && (
              <Button
                variant="secondary"
                onClick={() => {
                  setErrors(NO_ERRORS)
                  setSaved(false)
                  setEditing(true)
                }}
              >
                <Pencil className="h-4 w-4" aria-hidden />
                Edit
              </Button>
            )}
            {canDelete && (
              <Button variant="danger" onClick={() => setConfirming(true)}>
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            )}
          </>
        }
      />

      {/* Announced rather than shown as a toast that a screen reader misses. */}
      <p aria-live="polite" className="sr-only">
        {saved ? "Intervention saved." : ""}
      </p>
      {saved && !editing && (
        <div className="mb-4 rounded-md border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          Saved.
        </div>
      )}

      <InterventionView intervention={intervention} />

      <div className="mt-6">
        <DocumentsPanel parentType="interventions" parentId={id} />
      </div>

      {editing && (
        <InterventionEditorModal
          intervention={intervention}
          errors={errors}
          pending={update.isPending}
          onSubmit={submit}
          onClose={() => {
            setErrors(NO_ERRORS)
            setEditing(false)
          }}
        />
      )}

      <ConfirmDelete
        open={confirming}
        title={`Delete ${intervention.name}?`}
        description="The intervention leaves the catalogue permanently. Set its status to Inactive instead if past training records should keep pointing at it."
        pending={remove.isPending}
        error={remove.error ? submitErrorsFrom(remove.error).message : null}
        onCancel={() => {
          remove.reset()
          setConfirming(false)
        }}
        onConfirm={() =>
          remove.mutate(id, {
            onSuccess: () => void navigate({ to: "/interventions", replace: true }),
            // Deliberately not closing: a delete the server refused, most
            // likely because a training event still points at this
            // intervention, must say so rather than close as if it worked.
          })
        }
      />
    </>
  )
}

function BackLink() {
  return (
    <Link
      to="/interventions"
      className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      All interventions
    </Link>
  )
}

function DetailSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading the intervention</span>
      {[0, 1, 2].map((section) => (
        <Card key={section}>
          <CardContent className="py-5">
            <Skeleton className="mb-4 h-4 w-40" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[0, 1, 2, 3].map((row) => (
                <Skeleton key={row} className="h-10" />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
